import posthog from "posthog-js";
import { Component, ErrorInfo, ReactNode } from "react";
import ErrorPage from "./ErrorPage";

function captureErrorToPostHog(
  eventName: string,
  properties: Record<string, unknown>
): void {
  if (typeof posthog?.capture === "function") {
    posthog.capture(eventName, properties);
  }
}

function safeString(value: unknown, maxLen = 2000): string {
  if (value == null) return "";
  const s = typeof value === "string" ? value : String(value);
  return s.length > maxLen ? s.slice(0, maxLen) + "…" : s;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  showDebugInfo?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  globalError?: {
    message: string;
    source: string;
    lineno: number;
    colno: number;
    error: Error | null;
  };
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };

    // Bind the error handling methods to the class instance
    this.handleGlobalError = this.handleGlobalError.bind(this);
    this.handleUnhandledRejection = this.handleUnhandledRejection.bind(this);

    // Attach global error listeners
    window.addEventListener("error", this.handleGlobalError);
    window.addEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ error, errorInfo });
    console.error("React Error caught by ErrorBoundary:", error, errorInfo);
    captureErrorToPostHog("error_caught", {
      error_type: "react",
      message: safeString(error?.message),
      stack: safeString(error?.stack),
      component_stack: safeString(errorInfo?.componentStack),
      error_name: error?.name ?? undefined
    });
  }

  componentWillUnmount() {
    // Remove global error listeners
    window.removeEventListener("error", this.handleGlobalError);
    window.removeEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );
  }

  handleGlobalError(event: ErrorEvent) {
    this.setState({
      hasError: true,
      globalError: {
        message: event.message,
        source: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      }
    });
    console.error(
      "Global Error:",
      event.message,
      event.filename,
      event.lineno,
      event.colno,
      event.error
    );
    const err = event.error;
    captureErrorToPostHog("error_caught", {
      error_type: "global",
      message: safeString(event.message),
      source: safeString(event.filename),
      lineno: event.lineno,
      colno: event.colno,
      stack: err && err instanceof Error ? safeString(err.stack) : undefined,
      error_name: err && err instanceof Error ? err.name : undefined
    });
  }

  handleUnhandledRejection(event: PromiseRejectionEvent) {
    this.setState({
      hasError: true,
      globalError: {
        message:
          event.reason instanceof Error
            ? event.reason.message
            : "Unhandled Promise Rejection",
        source: "",
        lineno: 0,
        colno: 0,
        error: event.reason instanceof Error ? event.reason : null
      }
    });
    console.error("Unhandled Rejection:", event.reason);
    const reason = event.reason;
    const isError = reason instanceof Error;
    captureErrorToPostHog("error_caught", {
      error_type: "unhandled_rejection",
      message: isError ? safeString(reason.message) : safeString(reason),
      stack: isError ? safeString(reason.stack) : undefined,
      error_name: isError ? reason.name : undefined
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorPage
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          globalError={this.state.globalError}
          showDebugInfo={this.props.showDebugInfo}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
